import { useEffect, useRef, useState } from 'react';
import { AlertCircle, Check, CheckCircle2, Clock3, Copy, Link2, Loader2, Mail, RotateCw, XCircle } from 'lucide-react';
import { ErrorReportButton } from '@/components/ErrorReportPanel';
import type { InlineError } from '@/lib/errorReport';
import { copyText } from '@/lib/clipboard';
import { toast } from '@/lib/toast';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { QuoteReviewState } from '@/types/review';

interface QuoteReviewDialogProps {
  open: boolean;
  state: QuoteReviewState | null;
  loading: boolean;
  requesting: boolean;
  /** `productId::groepId` van de keuzegroep die nu wordt opgeslagen. */
  assigning: string | null;
  error: InlineError | null;
  onOpenChange: (open: boolean) => void;
  onAssign: (productId: string, groupId: string, ownerId: string | null) => void;
  onRequest: () => void;
  onRefresh: () => void;
  onContinueToPublish: () => void;
}

/** Radix accepteert geen lege waarde voor "nog niemand gekozen". */
const UNASSIGNED = '__unassigned__';

const statusCopy: Record<string, { label: string; className: string }> = {
  not_requested: { label: 'Nog niet gevraagd', className: 'bg-muted text-muted-foreground' },
  pending: { label: 'Wacht op reactie', className: 'bg-amber-100 text-amber-800' },
  approved: { label: 'Akkoord', className: 'bg-emerald-100 text-emerald-800' },
  rejected: { label: 'Wijzigingen gevraagd', className: 'bg-red-100 text-red-800' },
};

export default function QuoteReviewDialog({
  open,
  state,
  loading,
  requesting,
  assigning,
  error,
  onOpenChange,
  onAssign,
  onRequest,
  onRefresh,
  onContinueToPublish,
}: QuoteReviewDialogProps) {
  const [copiedOwnerId, setCopiedOwnerId] = useState<string | null>(null);
  const copiedTimer = useRef<number | null>(null);
  const pending = state?.reviewers.filter((reviewer) => reviewer.status === 'pending').length ?? 0;
  const openGroups = state?.groups.filter((group) => !group.selectedOwnerId) ?? [];
  const hasReviewUrls = Boolean(state?.reviewers.some((reviewer) => reviewer.reviewUrl));
  const approved = state?.reviewers.filter((reviewer) => reviewer.status === 'approved').length ?? 0;

  useEffect(() => () => {
    if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
  }, []);

  const copyReviewUrl = async (ownerId: string, reviewUrl: string) => {
    try {
      await copyText(reviewUrl);
      setCopiedOwnerId(ownerId);
      toast.success('Reviewlink gekopieerd');
      if (copiedTimer.current) window.clearTimeout(copiedTimer.current);
      copiedTimer.current = window.setTimeout(() => setCopiedOwnerId(null), 2000);
    } catch {
      toast.error('Reviewlink kopiëren is mislukt');
    }
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !requesting && onOpenChange(next)}>
      <DialogContent className="max-w-[calc(100vw-2rem)] overflow-hidden sm:max-w-[620px]">
        <DialogHeader>
          <DialogTitle>Review vóór publiceren</DialogTitle>
          <DialogDescription>
            Reviewers zijn afgeleid van de producten in deze offerte. Verplichte reviewers geven
            allemaal akkoord; uit een keuzegroep beoordeelt de verantwoordelijke die je hier kiest.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex min-h-48 items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Reviewstatus laden…
          </div>
        ) : error && !state ? (
          <div className="flex min-h-48 flex-col items-center justify-center gap-3 px-6 text-center">
            <AlertCircle className="h-6 w-6 text-destructive" />
            <p className="text-sm text-muted-foreground">
              {error.message} <ErrorReportButton report={error.report} />
            </p>
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RotateCw className="mr-2 h-4 w-4" />
              Opnieuw proberen
            </Button>
          </div>
        ) : state ? (
          <div className="min-w-0 space-y-4 overflow-hidden">
            {state.delivery && state.delivery.failed > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  {state.delivery.failed === state.reviewers.length
                    ? 'Geen van de reviewmails kon worden afgeleverd. Controleer de mailconfiguratie en verstuur nieuwe links.'
                    : `${state.delivery.failed} van de ${state.reviewers.length} reviewmails kon niet worden afgeleverd. Verstuur nieuwe links nadat de mailconfiguratie is gecontroleerd.`}
                </AlertDescription>
              </Alert>
            )}
            {state.status === 'approved' && (
              <Alert className="border-emerald-200 bg-emerald-50 text-emerald-900">
                <CheckCircle2 className="h-4 w-4" />
                <AlertDescription>
                  Alle {approved} reviewers zijn akkoord. Deze offerte kan naar de klant.
                </AlertDescription>
              </Alert>
            )}
            {state.status === 'pending' && (
              <Alert className="border-amber-200 bg-amber-50 text-amber-900">
                <Clock3 className="h-4 w-4" />
                <AlertDescription>
                  Reviewronde {state.version}: nog {pending} van {state.reviewers.length} reacties open.
                </AlertDescription>
              </Alert>
            )}
            {state.status === 'changes_requested' && (
              <Alert variant="destructive">
                <XCircle className="h-4 w-4" />
                <AlertDescription>
                  {pending > 0
                    ? `Er zijn wijzigingen gevraagd. Nog ${pending} van ${state.reviewers.length} reviewers kunnen via hun persoonlijke link reageren.`
                    : 'Er zijn wijzigingen gevraagd. Verwerk de feedback en start daarna een nieuwe reviewronde.'}
                </AlertDescription>
              </Alert>
            )}
            {state.status === 'invalidated' && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Er is iets gewijzigd sinds de vorige review — productregels, bedragen of de
                  aangewezen verantwoordelijke. Hieronder staat wie er nú moet beoordelen; start een
                  nieuwe ronde om ze een link te sturen.
                </AlertDescription>
              </Alert>
            )}
            {openGroups.length > 0 && (
              <Alert className="border-amber-200 bg-amber-50 text-amber-900">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  Kies eerst wie de review doet namens{' '}
                  {openGroups
                    .map((group) => `${group.group} (${group.productName})`)
                    .join(', ')}
                  .
                </AlertDescription>
              </Alert>
            )}
            {error && (
              <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error.message} <ErrorReportButton report={error.report} />
              </p>
            )}

            {state.groups.length > 0 && (
              <div className="min-w-0 space-y-3 rounded-md border bg-muted/30 p-4">
                <div>
                  <p className="text-sm font-medium">Verantwoordelijke per keuzegroep</p>
                  <p className="text-xs text-muted-foreground">
                    Kies wie namens de groep beoordeelt. Een andere keuze maakt een lopende
                    reviewronde ongeldig.
                  </p>
                </div>
                {state.groups.map((group) => {
                  const key = `${group.productId}::${group.groupId}`;
                  return (
                    <div
                      key={key}
                      className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-medium">{group.group}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {group.productName} · 1 van {group.options.length}
                        </span>
                      </span>
                      <div className="flex shrink-0 items-center gap-2">
                        {assigning === key && (
                          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                        )}
                        <Select
                          value={group.selectedOwnerId ?? UNASSIGNED}
                          disabled={requesting || assigning === key}
                          onValueChange={(value) =>
                            onAssign(
                              group.productId,
                              group.groupId,
                              value === UNASSIGNED ? null : value,
                            )
                          }
                        >
                          <SelectTrigger
                            className="h-9 w-full bg-background text-sm sm:w-[240px]"
                            aria-label={`Verantwoordelijke voor ${group.group} bij ${group.productName}`}
                          >
                            <SelectValue placeholder="Kies een verantwoordelijke" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={UNASSIGNED}>Nog niemand gekozen</SelectItem>
                            {group.options.map((option) => (
                              <SelectItem key={option.ownerId} value={option.ownerId}>
                                {option.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="min-w-0 overflow-hidden rounded-md border">
              {state.reviewers.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-muted-foreground">
                  Nog geen reviewers: kies eerst hierboven wie namens elke keuzegroep beoordeelt.
                </p>
              ) : null}
              {state.reviewers.map((reviewer) => {
                const visual = statusCopy[reviewer.status] || statusCopy.not_requested;
                return (
                  <div key={reviewer.ownerId} className="min-w-0 overflow-hidden border-b px-4 py-3 last:border-b-0">
                    <div className="flex min-w-0 items-start gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#E0F3FA] text-xs font-semibold text-[#006B7C]">
                        {reviewer.name.split(/\s+/).slice(0, 2).map((part) => part[0]).join('').toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-medium">{reviewer.name}</span>
                        <span className="block truncate text-xs text-muted-foreground">
                          {reviewer.productNames.join(', ')}
                        </span>
                      </span>
                      <Badge className={visual.className} variant="secondary">{visual.label}</Badge>
                    </div>
                    {reviewer.feedback && (
                      <p className="mt-2 whitespace-pre-wrap break-words rounded bg-red-50 px-3 py-2 text-sm text-red-900">
                        {reviewer.feedback}
                      </p>
                    )}
                    {reviewer.reviewUrl && (
                      <div className="mt-3 flex min-w-0 items-center gap-2 sm:pl-11">
                        <div className="flex h-9 min-w-0 flex-1 items-center gap-2 rounded-md bg-muted/60 px-3 text-xs text-muted-foreground">
                          <Link2 className="h-3.5 w-3.5 shrink-0" />
                          <span className="truncate" title={reviewer.reviewUrl}>{reviewer.reviewUrl}</span>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="shrink-0"
                          aria-label={`Reviewlink van ${reviewer.name} kopiëren`}
                          onClick={() => void copyReviewUrl(reviewer.ownerId, reviewer.reviewUrl!)}
                        >
                          {copiedOwnerId === reviewer.ownerId ? (
                            <Check className="h-4 w-4 text-emerald-600" />
                          ) : (
                            <Copy className="h-4 w-4" />
                          )}
                          {copiedOwnerId === reviewer.ownerId ? 'Gekopieerd' : 'Kopiëren'}
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <p className="text-xs text-muted-foreground">
              {hasReviewUrls
                ? 'Reviewlinks verlopen niet en zijn persoonlijk. Een wijziging aan gekoppelde producten, uren, bedragen of de verantwoordelijke maakt de huidige reviewronde ongeldig.'
                : 'Persoonlijke reviewlinks verschijnen hier zodra je de reviewronde start; dan kun je ze ook kopiëren.'}
            </p>
          </div>
        ) : null}

        <DialogFooter className="min-w-0 gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={requesting}>
            Sluiten
          </Button>
          {state?.status === 'pending' ? (
            <>
              <Button variant="outline" onClick={onRefresh} disabled={requesting}>
                <RotateCw className="mr-2 h-4 w-4" />
                Status vernieuwen
              </Button>
              <Button variant="outline" onClick={onRequest} disabled={requesting || !state.canRequest}>
                {requesting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
                Nieuwe links versturen
              </Button>
            </>
          ) : state?.canPublish ? (
            <Button onClick={onContinueToPublish}>
              <Check className="mr-2 h-4 w-4" />
              Doorgaan met publiceren
            </Button>
          ) : state?.required ? (
            <Button onClick={onRequest} disabled={requesting || !state.canRequest}>
              {requesting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
              {state.version ? 'Nieuwe reviewronde starten' : 'Ter review versturen'}
            </Button>
          ) : null}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
